import type { Variants, Transition } from 'framer-motion';
import type { DurationPreset, EasingPreset, StaggerPreset } from './types';

/**
 * Duration map (seconds) — mirrors CSS vars from theme.css (lines 211-217)
 */
export const durations: Record<DurationPreset, number> = {
  instant: 0.05,
  rapid: 0.1,
  fast: 0.15,
  normal: 0.2,
  moderate: 0.25,
  slow: 0.35,
  deliberate: 0.5,
};

/**
 * Easing curves — mirrors CSS vars from theme.css (lines 220-234)
 * Array format: [x1, y1, x2, y2] for cubic-bezier
 */
export const easings: Record<EasingPreset, [number, number, number, number]> = {
  sharp: [0.4, 0, 0.6, 1],
  sharpIn: [0.4, 0, 1, 1],
  sharpOut: [0, 0, 0.6, 1],
  smooth: [0.4, 0, 0.2, 1],
  smoothIn: [0.4, 0, 1, 1],
  smoothOut: [0, 0, 0.2, 1],
  emphasized: [0.2, 0, 0, 1.1],
};

/**
 * Stagger delays (seconds) — mirrors CSS vars from theme.css (lines 237-239)
 */
export const staggers: Record<StaggerPreset, number> = {
  fast: 0.04,
  normal: 0.08,
  slow: 0.12,
};

/** Build a transition config from presets */
export function buildTransition(
  duration: DurationPreset = 'fast',
  easing: EasingPreset = 'smooth',
  delay?: number
): Transition {
  return {
    duration: durations[duration],
    ease: easings[easing],
    ...(delay != null && { delay }),
  };
}

// ---------------------------------------------------------------------------
// Variant presets — GPU-only properties (opacity, x, y, scale)
// ---------------------------------------------------------------------------

export const fadeVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

export const slideUpVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -24 },
};

export const slideDownVariants: Variants = {
  hidden: { opacity: 0, y: -24 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 24 },
};

export const slideLeftVariants: Variants = {
  hidden: { opacity: 0, x: 24 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -24 },
};

export const slideRightVariants: Variants = {
  hidden: { opacity: 0, x: -24 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 24 },
};

export const scaleVariants: Variants = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.92 },
};

/** Variant for staggered list items */
export const listItemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
};

/** Direction → variant mapping */
export const directionVariants = {
  up: slideUpVariants,
  down: slideDownVariants,
  left: slideLeftVariants,
  right: slideRightVariants,
  none: scaleVariants,
} as const;

/**
 * Create a parent variant that staggers its children.
 * Children must use their own variants (e.g. listItemVariants).
 */
export function createStaggerParent(
  stagger: StaggerPreset | number = 'normal',
  delayChildren = 0
): Variants {
  const staggerValue = typeof stagger === 'number' ? stagger : staggers[stagger];
  return {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: staggerValue,
        delayChildren,
      },
    },
  };
}
